import { useState } from "react";

const CATEGORY_OPTIONS = [
  "ביטוח מנהלים", "קרן פנסיה", "ביטוח חיים", "קופת גמל",
  "קרן השתלמות", "קופת גמל להשקעה", "חיסכון לכל ילד",
];

// month input value "YYYY-MM" → "01/MM/YYYY" (recordSnapshot reads DD/MM/YYYY)
const monthToDate = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `01/${m}/${y}`;
};
const dateToMonth = (d) => {
  const m = String(d || "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2]}` : "";
};
const num = (v) => (v === "" || v == null ? null : Number(v));

const emptyForm = () => {
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return {
    companyName: "", cat: "", tochnit: "",
    joinMonth: "", balance: "", deposits: "", valueMonth: ym,
    weight: "", stock: "", foreign: "", forex: "", liquid: "",
  };
};

function fundToForm(f) {
  const e = f._manualExposure || {};
  return {
    companyName: f.companyName || "", cat: f.cat || "", tochnit: f.tochnit || "",
    joinMonth: dateToMonth(f.joinDate), balance: f.redeem ?? "",
    deposits: f.deposits ?? "", valueMonth: dateToMonth(f.validDate),
    weight: f.weight ?? "",
    stock: e.stock ?? "", foreign: e.foreign ?? "", forex: e.forex ?? "", liquid: e.liquid ?? "",
  };
}

// Manage a client's manually-entered products (add / edit / delete). Manual
// products live in the client's holdings funds, so they feed totals, charts,
// exposure and monthly history like any imported fund.
export default function ManualProductModal({ clientId, clientName, funds, onAdd, onUpdate, onRemove, onClose, onLogActivity }) {
  const manualFunds = (funds || []).filter(f => f._manual);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const buildFund = (id) => ({
    id,
    _manual: true,
    _sourceType: "ידני",
    companyName: form.companyName.trim(),
    cat: form.cat.trim(),
    tochnit: form.tochnit.trim(),
    joinDate: monthToDate(form.joinMonth),
    redeem: num(form.balance) || 0,
    deposits: num(form.deposits),
    validDate: monthToDate(form.valueMonth),
    statusLabel: "פעיל",
    weight: num(form.weight),
    _manualExposure: {
      stock: num(form.stock), foreign: num(form.foreign),
      forex: num(form.forex), liquid: num(form.liquid),
    },
    maslulim: [{ name: form.tochnit.trim() || form.companyName.trim() || "מוצר ידני", balance: num(form.balance) || 0 }],
  });

  const handleSave = () => {
    if (editingId) {
      const patch = buildFund(editingId);
      onUpdate(clientId, editingId, patch);
    } else {
      const id = `manual_${clientId}_${Date.now()}`;
      onAdd(clientId, buildFund(id));
      onLogActivity?.({
        type: "note", description: `נוסף מוצר ידני: ${form.tochnit || form.companyName || "מוצר"}`,
        clientName, date: new Date().toISOString().slice(0, 10),
      });
    }
    setForm(emptyForm());
    setEditingId(null);
  };

  const startEdit = (f) => { setForm(fundToForm(f)); setEditingId(f.id); };
  const cancelEdit = () => { setForm(emptyForm()); setEditingId(null); };

  const canSave = form.companyName.trim() || form.tochnit.trim() || num(form.balance);

  const lbl = { fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3, display: "block" };
  const inp = { width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13, boxSizing: "border-box" };
  const field = (label, node) => <div><label style={lbl}>{label}</label>{node}</div>;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, direction: "rtl" }} onClick={onClose}>
      <div style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0e7490, #0891b2)", padding: "16px 20px", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>➕ הוספת מוצר ידני</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{clientName} · מוצר שאינו מגיע מקובץ (פק"מ, תיק בנקאי וכו')</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "white", width: 30, height: 30, borderRadius: 8, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 20, overflowY: "auto" }}>
          {/* Form */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
            {field("חברה", <input style={inp} value={form.companyName} onChange={set("companyName")} placeholder="למשל בנק לאומי" />)}
            {field("קטגוריה", <>
              <input style={inp} value={form.cat} onChange={set("cat")} list="manual-cat-list" placeholder="בחר או הקלד" />
              <datalist id="manual-cat-list">{CATEGORY_OPTIONS.map(c => <option key={c} value={c} />)}</datalist>
            </>)}
            {field("שם מוצר", <input style={inp} value={form.tochnit} onChange={set("tochnit")} />)}
            {field("תחילת ותק", <input style={inp} type="month" value={form.joinMonth} onChange={set("joinMonth")} />)}
            {field("צבירה (₪)", <input style={inp} type="number" value={form.balance} onChange={set("balance")} />)}
            {field("הפקדות (₪)", <input style={inp} type="number" value={form.deposits} onChange={set("deposits")} />)}
            {field("תאריך ערך", <input style={inp} type="month" value={form.valueMonth} onChange={set("valueMonth")} />)}
            {field("משקל (%)", <input style={inp} type="number" value={form.weight} onChange={set("weight")} />)}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", margin: "16px 0 8px" }}>חשיפות (רשות — נכנס לווידג'ט חשיפת התיק)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
            {field("מניות (%)", <input style={inp} type="number" value={form.stock} onChange={set("stock")} />)}
            {field("חו\"ל (%)", <input style={inp} type="number" value={form.foreign} onChange={set("foreign")} />)}
            {field("מט\"ח (%)", <input style={inp} type="number" value={form.forex} onChange={set("forex")} />)}
            {field("נכסים נזילים (%)", <input style={inp} type="number" value={form.liquid} onChange={set("liquid")} />)}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button onClick={handleSave} disabled={!canSave} style={{ background: canSave ? "#0e7490" : "#cbd5e1", color: "white", border: "none", padding: "9px 20px", borderRadius: 8, cursor: canSave ? "pointer" : "default", fontWeight: 700, fontSize: 14 }}>
              {editingId ? "💾 עדכן מוצר" : "➕ הוסף מוצר"}
            </button>
            {editingId && (
              <button onClick={cancelEdit} style={{ background: "white", color: "#334155", border: "1px solid #cbd5e1", padding: "9px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 14 }}>ביטול עריכה</button>
            )}
          </div>

          {/* Existing manual products */}
          {manualFunds.length > 0 && (
            <div style={{ marginTop: 22 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>מוצרים ידניים קיימים ({manualFunds.length})</div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                {manualFunds.map((f, i) => (
                  <div key={f.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderTop: i ? "1px solid #f1f5f9" : "none", background: editingId === f.id ? "#ecfeff" : "white" }}>
                    <div style={{ fontSize: 13 }}>
                      <span style={{ fontWeight: 700 }}>{f.tochnit || f.companyName || "מוצר"}</span>
                      <span style={{ color: "#64748b" }}> · {f.cat || "—"} · {f.companyName || "—"} · ₪{(f.redeem || 0).toLocaleString("he-IL")}</span>
                      {f.validDate && <span style={{ color: "#94a3b8" }}> · {f.validDate}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => startEdit(f)} title="ערוך" style={{ border: "1px solid #cbd5e1", background: "white", borderRadius: 6, cursor: "pointer", padding: "3px 8px", fontSize: 13 }}>✎</button>
                      <button onClick={() => onRemove(clientId, f.id)} title="מחק" style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", borderRadius: 6, cursor: "pointer", padding: "3px 8px", fontSize: 13 }}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
